import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Gamepad2, Users, Plus, ChevronRight, Loader2, Dice5, Spade, Layers, Grid3X3, Crown, Hash, Sparkles } from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";

const GAME_ICONS = {
  "okey-101": Dice5,
  "batak": Spade,
  "pisti": Layers,
  "tavla": Gamepad2,
  "dama": Grid3X3,
  "king": Crown,
  "altmisalti": Hash,
  "domino": Gamepad2,
};

export default function Games() {
  const { user } = useAuth();
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchGames = async () => {
      try {
        const response = await api.getGames();
        setGames(response.data);
      } catch (error) {
        console.error("Error fetching games:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchGames();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 text-[#4DFFFF] animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="games-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold font-['Barlow_Condensed'] uppercase tracking-wider text-white">
            Oyunlar
          </h1>
          <p className="text-[#B1C1C1] mt-1">Desteklenen tüm oyunları keşfet</p>
        </div>
        {user && (
          <Link to="/create-custom-game">
            <Button className="bg-[#C6A87C] text-[#050E2E] hover:bg-[#C6A87C]/90 font-['Barlow_Condensed'] uppercase tracking-wider font-bold">
              <Sparkles className="w-4 h-4 mr-2" />
              Özel Oyun
            </Button>
          </Link>
        )}
      </div>

      {/* Custom Games Section */}
      {games.some(g => !g.is_system) && (
        <div className="mb-8">
          <h2 className="text-lg font-bold font-['Barlow_Condensed'] uppercase tracking-wider text-[#C6A87C] mb-4">
            Özel Oyunlar
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {games.filter(g => !g.is_system).map((game, index) => {
              const IconComponent = GAME_ICONS[game.game_id] || Gamepad2;
              return (
                <div
                  key={game.game_id}
                  className="glass-card glass-card-hover overflow-hidden animate-fade-in-up border-[#C6A87C]/30"
                  style={{ animationDelay: `${index * 0.05}s` }}
                  data-testid={`custom-game-card-${game.game_id}`}
                >
                  <div 
                    className="h-24 relative"
                    style={{ background: `linear-gradient(135deg, ${game.color}33 0%, transparent 100%)` }}
                  >
                    <Sparkles className="absolute right-4 bottom-4 w-8 h-8 text-[#C6A87C] opacity-50" />
                  </div>
                  <div className="p-4">
                    <div className="flex items-center gap-3 mb-2">
                      <div 
                        className="w-8 h-8 rounded-lg flex items-center justify-center"
                        style={{ backgroundColor: `${game.color}33` }}
                      >
                        <Gamepad2 className="w-4 h-4" style={{ color: game.color }} />
                      </div>
                      <div>
                        <h3 className="text-base font-bold font-['Barlow_Condensed'] uppercase tracking-wider text-white">
                          {game.name_tr || game.name}
                        </h3>
                        <p className="text-xs text-[#64748B]">
                          {game.min_players}-{game.max_players} Oyuncu
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center justify-end">
                      <Link to={`/create-league?game=${game.game_id}`}>
                        <Button size="sm" className="bg-[#4DFFFF] text-[#050E2E] hover:bg-[#4DFFFF]/90">
                          <Plus className="w-3 h-3 mr-1" />
                          Lig Oluştur
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* System Games Section */}
      <h2 className="text-lg font-bold font-['Barlow_Condensed'] uppercase tracking-wider text-white mb-4">
        Sistem Oyunları
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {games.filter(g => g.is_system !== false).map((game, index) => {
          const IconComponent = GAME_ICONS[game.game_id] || Gamepad2;
          return (
            <div
              key={game.game_id}
              className="glass-card glass-card-hover overflow-hidden animate-fade-in-up"
              style={{ animationDelay: `${index * 0.05}s` }}
              data-testid={`game-card-${game.game_id}`}
            >
              <div 
                className="h-32 relative"
                style={{ background: `linear-gradient(135deg, ${game.color}33 0%, transparent 100%)` }}
              >
                <IconComponent 
                  className="absolute right-4 bottom-4 w-16 h-16 opacity-30"
                  style={{ color: game.color }}
                />
              </div>
              <div className="p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div 
                    className="w-10 h-10 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: `${game.color}33` }}
                  >
                    <IconComponent className="w-5 h-5" style={{ color: game.color }} />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold font-['Barlow_Condensed'] uppercase tracking-wider text-white">
                      {game.name_tr || game.name}
                    </h3>
                    <p className="text-xs text-[#64748B]">
                      {game.min_players}-{game.max_players} Oyuncu
                    </p>
                  </div>
                </div>
                <p className="text-sm text-[#B1C1C1] mb-4 line-clamp-2">
                  {game.description}
                </p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-[#64748B]" />
                    <span className="text-xs text-[#64748B]">
                      {game.min_players === game.max_players 
                        ? `${game.min_players} oyuncu` 
                        : `${game.min_players}-${game.max_players} oyuncu`
                      }
                    </span>
                  </div>
                  <Link to={`/create-league?game=${game.game_id}`}>
                    <Button size="sm" className="bg-[#4DFFFF] text-[#050E2E] hover:bg-[#4DFFFF]/90">
                      <Plus className="w-3 h-3 mr-1" />
                      Lig Oluştur
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
